/** Resident lifecycle states: the values the database holds, in the order the roster's chips list
 * them. A filter that cannot name one hides residents. Where somebody physically is — in the
 * building, out, in hospital — is `presenceStatus`, a separate column. */
export const RESIDENT_STATUSES = [
  "active",
  "inactive",
  "discharged",
  "out_of_facility_active",
  "out_of_facility_hold",
  "pending",
  "never_admitted",
] as const;
export type ResidentStatus = (typeof RESIDENT_STATUSES)[number];

/** A chart opened from a lead before move-in so a deposit can be billed: nobody lives here yet. */
export const PENDING_RESIDENT_STATUS = "pending" satisfies ResidentStatus;
/** A pending chart whose lead closed with no money received: it was never a stay. */
export const NEVER_ADMITTED_STATUS = "never_admitted" satisfies ResidentStatus;

/** Charts of somebody who never moved in: never a move-in, a move-out, a stay or a census line. */
export const NOT_MOVED_IN_STATUSES: readonly ResidentStatus[] = [
  PENDING_RESIDENT_STATUS,
  NEVER_ADMITTED_STATUS,
];

export function residentMovedIn(status: string): boolean {
  return !(NOT_MOVED_IN_STATUSES as readonly string[]).includes(status);
}

/** Only the lead moves a chart into or out of these, so nobody reaches the MAR without the move-in
 * carry. */
export function whyResidentStatusChangeRefused(
  from: string,
  to: string | undefined,
): string | null {
  if (to === undefined || to === from) return null;
  if (!residentMovedIn(to)) {
    return "A chart becomes pending only when it is opened from its lead (crm_leads_openChart).";
  }
  if (!residentMovedIn(from)) {
    return "This chart belongs to a lead that has not moved in. Move the person in with crm_leads_convert, or close the lead.";
  }
  return null;
}

/** Charts whose person is not in the building's care, so never on a census, MAR or care roster. */
export const NOT_IN_CARE_STATUSES: readonly ResidentStatus[] = [
  "discharged",
  "inactive",
  ...NOT_MOVED_IN_STATUSES,
];

/** The roster's sortable columns. Here rather than beside the list's schema so the screen's search
 * parser can read them without loading zod. */
export const RESIDENT_SORT_KEYS = [
  "name",
  "room",
  "dateOfBirth",
  "gender",
  "status",
  "payerType",
  "admissionDate",
  "facility",
] as const;

const LABELS: Record<ResidentStatus, string> = {
  active: "Active",
  inactive: "Inactive",
  discharged: "Discharged",
  out_of_facility_active: "Out of facility, active",
  out_of_facility_hold: "Out of facility, hold",
  pending: "Pending move-in",
  never_admitted: "Never admitted",
};

// The column is free text and carries older values, so an unknown one is made
// readable rather than printed raw: a census handed to an inspector must not
// say "out_of_facility_hold".
export function residentStatusLabel(status: string): string {
  const known = (LABELS as Record<string, string>)[status];
  if (known) return known;
  if (!status) return "";
  return status.replaceAll("_", " ").replace(/^\w/, (first) => first.toUpperCase());
}

export type StatusTone = "ok" | "info" | "warn" | "crit" | "neutral";

const TONES: Record<ResidentStatus, StatusTone> = {
  active: "ok",
  inactive: "neutral",
  discharged: "warn",
  out_of_facility_active: "info",
  out_of_facility_hold: "warn",
  pending: "info",
  never_admitted: "neutral",
};

/** Unknown values fall to neutral: a new status renders quietly rather than vanishing. */
export function residentStatusTone(status: string | null | undefined): StatusTone {
  if (!status) return "neutral";
  return (TONES as Record<string, StatusTone>)[status] ?? "neutral";
}

export const PAYER_TYPES = ["private_pay", "medicaid", "va", "na"] as const;
export type PayerType = (typeof PAYER_TYPES)[number];

const PAYER_LABELS: Record<PayerType, string> = {
  private_pay: "Private pay",
  medicaid: "Medicaid",
  va: "VA",
  na: "N/A",
};

// How a payer code reads. An unknown code falls through to itself rather than
// to a placeholder: a new payer type should look unstyled, not like missing
// data. Nothing recorded is "N/A", which is what the old list printed.
export function payerLabel(code: string | null | undefined): string {
  if (!code) return "N/A";
  return (PAYER_LABELS as Record<string, string>)[code] ?? code;
}
